/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ChangeOperation } from './ChangeOperation';
import {
    ChangeOperationFromJSON,
    ChangeOperationFromJSONTyped,
    ChangeOperationToJSON,
    ChangeOperationToJSONTyped,
} from './ChangeOperation';
import type { ChangeRequestStatus } from './ChangeRequestStatus';
import {
    ChangeRequestStatusFromJSON,
    ChangeRequestStatusFromJSONTyped,
    ChangeRequestStatusToJSON,
    ChangeRequestStatusToJSONTyped,
} from './ChangeRequestStatus';

/**
 * A Change Request represents a request to modify a Logistics Object.
 * It contains information about the requestor, the requested changes, and the status of the request.
 * 
 * @export
 * @interface ChangeRequest
 */
export interface ChangeRequest {
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof ChangeRequest
     */
    id?: string;
    /**
     * Identifier of the member that created the Change Request.
     * This is used to track who requested the changes.
     * 
     * @type {string}
     * @memberof ChangeRequest
     */
    requestorMemberIdentifier?: string;
    /**
     * Identifier of the user that created the Change Request.
     * This is used to track who requested the changes.
     * 
     * @type {string}
     * @memberof ChangeRequest
     */
    requestorUserId?: string;
    /**
     * Revision of the Logistics Object that the Change Request is based on.
     * This is used to ensure that the changes are applied to the correct version of the Logistics Object.
     * 
     * @type {number}
     * @memberof ChangeRequest
     */
    logisticsObjectRevision?: number;
    /**
     * 
     * @type {ChangeRequestStatus}
     * @memberof ChangeRequest
     */
    status?: ChangeRequestStatus;
    /**
     * A description of the Change Request.
     * This should provide context for the requested changes and any relevant information.
     * 
     * @type {string}
     * @memberof ChangeRequest
     */
    description?: string;
    /**
     * 
     * @type {Array<ChangeOperation>}
     * @memberof ChangeRequest
     */
    operations?: Array<ChangeOperation>;
    /**
     * Date and time, UTC ISO8601, when the ChangeRequest was created
     * @type {Date}
     * @memberof ChangeRequest
     */
    createdAt?: Date;
    /**
     * Date and time, UTC ISO8601, when the current ChangeRequest was last updated
     * @type {Date}
     * @memberof ChangeRequest
     */
    updatedAt?: Date;
    /**
     * The traceparent header value from the request that created the Change Request.
     * This allows tracing the creation of the Change Request across distributed systems.
     * 
     * @type {string}
     * @memberof ChangeRequest
     */
    creationTraceparent?: string;
    /**
     * The traceparent header value from the last request that updated the Change Request.
     * This allows tracing the updates to the Change Request across distributed systems.
     * 
     * @type {string}
     * @memberof ChangeRequest
     */
    updateTraceparent?: string;
}



/**
 * Check if a given object implements the ChangeRequest interface.
 */
export function instanceOfChangeRequest(value: object): value is ChangeRequest {
    return true;
}

export function ChangeRequestFromJSON(json: any): ChangeRequest {
    return ChangeRequestFromJSONTyped(json, false);
}

export function ChangeRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): ChangeRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'] == null ? undefined : json['id'],
        'requestorMemberIdentifier': json['requestorMemberIdentifier'] == null ? undefined : json['requestorMemberIdentifier'],
        'requestorUserId': json['requestorUserId'] == null ? undefined : json['requestorUserId'],
        'logisticsObjectRevision': json['logisticsObjectRevision'] == null ? undefined : json['logisticsObjectRevision'],
        'status': json['status'] == null ? undefined : ChangeRequestStatusFromJSON(json['status']),
        'description': json['description'] == null ? undefined : json['description'],
        'operations': json['operations'] == null ? undefined : ((json['operations'] as Array<any>).map(ChangeOperationFromJSON)),
        'createdAt': json['createdAt'] == null ? undefined : (new Date(json['createdAt'])),
        'updatedAt': json['updatedAt'] == null ? undefined : (new Date(json['updatedAt'])),
        'creationTraceparent': json['creationTraceparent'] == null ? undefined : json['creationTraceparent'],
        'updateTraceparent': json['updateTraceparent'] == null ? undefined : json['updateTraceparent'],
    };
}

export function ChangeRequestToJSON(json: any): ChangeRequest {
    return ChangeRequestToJSONTyped(json, false);
}

export function ChangeRequestToJSONTyped(value?: ChangeRequest | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'id': value['id'],
        'requestorMemberIdentifier': value['requestorMemberIdentifier'],
        'requestorUserId': value['requestorUserId'],
        'logisticsObjectRevision': value['logisticsObjectRevision'],
        'status': ChangeRequestStatusToJSON(value['status']),
        'description': value['description'],
        'operations': value['operations'] == null ? undefined : ((value['operations'] as Array<any>).map(ChangeOperationToJSON)),
        'createdAt': value['createdAt'] == null ? value['createdAt'] : value['createdAt'].toISOString(),
        'updatedAt': value['updatedAt'] == null ? value['updatedAt'] : value['updatedAt'].toISOString(),
        'creationTraceparent': value['creationTraceparent'],
        'updateTraceparent': value['updateTraceparent'],
    };
}

